import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { Vehicle, VehicleStatus } from '../../../../shared/types/vehicle.types';
import { vehicleStatusConfig } from '../../utils/vehicleStatus';
import useVehicle from '../../wrappers/use-vehicle';
import { useTranslation } from 'react-i18next';
import useDateFormat from '../../utils/useDateFormat';
import { vehicleRegistrationQueueStyles } from './VehicleRegistrationQueue.styles';

const VehicleRegistrationQueue: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const formatDate = useDateFormat();
    const [filter, setFilter] = useState<'pending' | 'all'>('pending');
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [loading, setLoading] = useState(true);
    const { list } = useVehicle();

    useEffect(() => {
        const fetchVehicles = async () => {
            setLoading(true);
            const data = await list();
            setVehicles(data);
            setLoading(false);
        };
        fetchVehicles();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredVehicles = vehicles.filter((v) => {
        if (filter === 'pending') return v.status === VehicleStatus.Pending || v.status === VehicleStatus.InfoRequested;
        return true;
    });

    return (
        <Box sx={vehicleRegistrationQueueStyles.root}>
            <Box sx={vehicleRegistrationQueueStyles.header}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>{t('vehicleRegistrationQueue.title')}</Typography>
                    <Typography color="text.secondary" mt={0.5}>{t('vehicleRegistrationQueue.subtitle')}</Typography>
                </Box>
                <ButtonGroup size="small" variant="outlined">
                    <Button onClick={() => setFilter('pending')} variant={filter === 'pending' ? 'contained' : 'outlined'}>{t('vehicleRegistrationQueue.filterPending')}</Button>
                    <Button onClick={() => setFilter('all')} variant={filter === 'all' ? 'contained' : 'outlined'}>{t('vehicleRegistrationQueue.filterAll')}</Button>
                </ButtonGroup>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={vehicleRegistrationQueueStyles.tableContainer}>
                <Table>
                    <TableHead>
                        <TableRow sx={vehicleRegistrationQueueStyles.tableHeaderRow}>
                            <TableCell sx={vehicleRegistrationQueueStyles.tableHeaderCell}>{t('vehicleRegistrationQueue.colMachine')}</TableCell>
                            <TableCell sx={vehicleRegistrationQueueStyles.tableHeaderCell}>{t('vehicleRegistrationQueue.colDealer')}</TableCell>
                            <TableCell sx={vehicleRegistrationQueueStyles.tableHeaderCell}>{t('vehicleRegistrationQueue.colDate')}</TableCell>
                            <TableCell sx={vehicleRegistrationQueueStyles.tableHeaderCell}>{t('vehicleRegistrationQueue.colStatus')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={vehicleRegistrationQueueStyles.loadingCell}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && filteredVehicles.map((vehicle) => {
                            const status = vehicleStatusConfig[vehicle.status];
                            return (
                                <TableRow key={vehicle.uid} hover onClick={() => navigate(`/app/admin/vehicle-registrations/${vehicle.uid}`)} sx={vehicleRegistrationQueueStyles.clickableRow}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{vehicle.model}</Typography>
                                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">{vehicle.vin}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{vehicle.dealer}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{formatDate(vehicle.createdAt)}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={status.label} color={status.color} size="small" />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!loading && filteredVehicles.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={vehicleRegistrationQueueStyles.emptyCell}>
                                    {t('vehicleRegistrationQueue.empty')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default VehicleRegistrationQueue;
